'use es6';

import { Fragment, useMemo, useEffect, createRef, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import PortalIdParser from 'PortalIdParser';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CALYPSO, BATTLESHIP, FLINT } from 'HubStyleTokens/colors';
import { URANUS_LAYER, CARD_SHADOW_OFFSET_Y, CARD_SHADOW_BLUR_RADIUS, CARD_SHADOW_ALPHA } from 'HubStyleTokens/sizes';
import { CARD_SHADOW_COLOR } from 'HubStyleTokens/theme';
import { rgba } from 'UIComponents/core/Color';
import UIButton from 'UIComponents/button/UIButton';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UICardSection from 'UIComponents/card/UICardSection';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIImage from 'UIComponents/image/UIImage';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import UITab from 'UIComponents/nav/UITab';
import UITabs from 'UIComponents/nav/UITabs';
import UILink from 'UIComponents/link/UILink';
import Big from 'UIComponents/elements/Big';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import MainSection from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { ProfileListing } from 'marketplace-ui-solutions-core/records/ProfileListing';
import * as CustomPropTypes from '../../constants/PropTypes';
import { PROFILE_LEFT_COL_SIZE, PROFILE_RIGHT_COL_SIZE, LOGO_WRAPPER_OFFSET, LOGO_WRAPPER_BOTTOM_MARGIN, LOGO_IMAGE_HEIGHT, LOGO_WRAPPER_PADDING_X, LOGO_WRAPPER_PADDING_Y } from '../../constants/ProfileConstants';
import { getEditProfileLink, getFullLocationUrl } from '../../utils/urls';
import { fromFilterStateToLocation } from '../../utils/storefrontFilters';
import { getMultiSelectStorefrontFilters } from '../../slices/storefrontFiltersSlice';
import { getServices } from '../../slices/serviceCatalogSlice';
import { getRecentActivity } from '../../utils/homepage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const I18N_BASE = 'ecosystem-marketplace-solutions-ui.profile';
const LOGO_IMAGE_STICKY_HEIGHT = 25;
const NAV_MIN_SCREEN_SIZE = ScreenSizes.MD;
const START_GRADIENT_COLOR = '#00497D';
const END_GRADIENT_COLOR = '#0FBFBF';
const ResponsiveMainSection = props => /*#__PURE__*/_jsx(MainSection, Object.assign({}, props, {
  responsive: true
}));
const BreadcrumbsWrapper = styled(ResponsiveMainSection).withConfig({
  displayName: "ProfileHeader__BreadcrumbsWrapper",
  componentId: "sc-1c4cti8-0"
})(["padding-top:0 !important;padding-bottom:", "px !important;background-color:", ";background-image:linear-gradient( -303deg,", ",", " );"], LOGO_WRAPPER_OFFSET, CALYPSO, START_GRADIENT_COLOR, END_GRADIENT_COLOR);
const PositionedEditButton = styled(UIButton).withConfig({
  displayName: "ProfileHeader__PositionedEditButton",
  componentId: "sc-1c4cti8-1"
})(["position:relative;top:33px;"]);
const LogoCardWrapper = styled(UICardWrapper).withConfig({
  displayName: "ProfileHeader__LogoCardWrapper",
  componentId: "sc-1c4cti8-2"
})(["position:relative;bottom:", "px;padding:", "px ", "px;margin-bottom:", "px;"], LOGO_WRAPPER_OFFSET, LOGO_WRAPPER_PADDING_Y, LOGO_WRAPPER_PADDING_X, LOGO_WRAPPER_BOTTOM_MARGIN);
const LogoImageWrapper = styled(UICardSection).withConfig({
  displayName: "ProfileHeader__LogoImageWrapper",
  componentId: "sc-1c4cti8-3"
})(["height:", "px;padding:0;> img{max-width:100%;}"], LOGO_IMAGE_HEIGHT);
const ScrollableHeaderWrapper = styled(ResponsiveMainSection).attrs(() => ({
  color: 'none'
})).withConfig({
  displayName: "ProfileHeader__ScrollableHeaderWrapper",
  componentId: "sc-1c4cti8-4"
})(["position:relative;margin-bottom:-", "px;padding-top:0;padding-bottom:0;"], LOGO_WRAPPER_OFFSET);
const StickyHeaderWrapper = styled(ResponsiveMainSection).withConfig({
  displayName: "ProfileHeader__StickyHeaderWrapper",
  componentId: "sc-1c4cti8-5"
})(["position:fixed;top:0;left:", ";z-index:", ";padding-top:0;padding-bottom:0;width:", ";border-bottom:1px solid ", ";box-shadow:0px ", " ", " 0 ", ";"], props => props.isInApp ? '236px' : 0, URANUS_LAYER, props => props.isInApp ? 'calc(100% - var(--global-nav-vertical-nav-width, 236px))' : '100%', BATTLESHIP, CARD_SHADOW_OFFSET_Y, CARD_SHADOW_BLUR_RADIUS, rgba(CARD_SHADOW_COLOR, CARD_SHADOW_ALPHA));
const ProfileHeader = ({
  activeTab,
  canonicalAppContext,
  isPrerender,
  isPreview,
  onTrackInteraction,
  profileListing,
  scrollToTabSection
}) => {
  const {
    logoUrl,
    name,
    portalId: providerPortalId
  } = profileListing;
  const storefrontFilters = useSelector(getMultiSelectStorefrontFilters);
  const catalogServices = useSelector(getServices);
  const tabsRef = /*#__PURE__*/createRef();
  const [tabsOffsetTop, setTabsOffsetTop] = useState(null);
  const [sticky, setSticky] = useState(false);
  const history = useHistory();
  const handleTrackInteraction = useCallback((action, extras = {}) => {
    onTrackInteraction(action, Object.assign({}, extras, {
      section: 'header'
    }));
  }, [onTrackInteraction]);
  const renderedBreadcrumbs = useMemo(() => {
    const filtersLocation = fromFilterStateToLocation(storefrontFilters, catalogServices);
    const renderedBreadcrumbText = /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${I18N_BASE}.back`
    });
    const fromHomepage = history.location.state && history.location.state.fromHomepage;
    let renderedBreadcrumbLink;
    if (isPrerender || isPreview) {
      renderedBreadcrumbLink = /*#__PURE__*/_jsx(UILink, {
        disabled: isPreview,
        href: getFullLocationUrl(filtersLocation, canonicalAppContext),
        use: "on-dark",
        children: renderedBreadcrumbText
      });
    } else {
      const recentActivity = getRecentActivity();
      const useQuestionnaireRef = recentActivity && recentActivity.type === 'questionnaire';
      if (useQuestionnaireRef) {
        filtersLocation.search = filtersLocation.search.concat('&ref=questionnaire');
      }
      renderedBreadcrumbLink = /*#__PURE__*/_jsx(UIRouterLink, {
        to: fromHomepage && '/' || filtersLocation // if coming from Homepage, redirect accordingly
        ,
        use: "on-dark",
        onClick: () => handleTrackInteraction('click breadcrumb link'),
        children: renderedBreadcrumbText
      });
    }
    return /*#__PURE__*/_jsx(UIBreadcrumbs, {
      className: "p-x-0 m-top-0",
      children: renderedBreadcrumbLink
    });
  }, [canonicalAppContext, catalogServices, handleTrackInteraction, history, isPrerender, isPreview, storefrontFilters]);
  const renderedEditButton = useMemo(() => {
    const editProfileHref = getEditProfileLink(providerPortalId);
    const renderedEditButtonText = /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${I18N_BASE}.edit`
    });
    return !isPreview && providerPortalId === PortalIdParser.get() ? /*#__PURE__*/_jsx(MediaQuery, {
      alternate: /*#__PURE__*/_jsx(UIButton, {
        href: editProfileHref,
        use: "primary-white",
        children: renderedEditButtonText
      }),
      minScreenSize: NAV_MIN_SCREEN_SIZE,
      children: /*#__PURE__*/_jsx(PositionedEditButton, {
        href: editProfileHref,
        use: "primary-white",
        children: renderedEditButtonText
      })
    }) : null;
  }, [isPreview, providerPortalId]);
  const handleTabClick = useCallback(tabId => {
    scrollToTabSection(tabId);
    handleTrackInteraction('click tab navigation', {
      value: tabId
    });
  }, [handleTrackInteraction, scrollToTabSection]);
  const renderedTabs = useMemo(() => {
    return /*#__PURE__*/_jsx(MediaQuery, {
      minScreenSize: NAV_MIN_SCREEN_SIZE,
      children: /*#__PURE__*/_jsxs(UITabs, {
        className: "p-top-4",
        selected: activeTab,
        children: [/*#__PURE__*/_jsx(UITab, {
          tabId: "about",
          onClick: () => handleTabClick('about'),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `${I18N_BASE}.tabs.about`
          })
        }), /*#__PURE__*/_jsx(UITab, {
          tabId: "reviews",
          onClick: () => handleTabClick('reviews'),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `${I18N_BASE}.tabs.reviews`
          })
        })]
      })
    });
  }, [activeTab, handleTabClick]);
  const renderedProfileImage = useMemo(() => {
    return logoUrl ? /*#__PURE__*/_jsx(UIImage, {
      alt: name,
      src: logoUrl,
      objectFit: "contain",
      style: {
        height: sticky ? LOGO_IMAGE_STICKY_HEIGHT : LOGO_IMAGE_HEIGHT
      }
    }) : /*#__PURE__*/_jsxs(UIFlex, {
      style: {
        height: '100%'
      },
      direction: sticky ? 'row' : 'column',
      align: "center",
      justify: "center",
      children: [/*#__PURE__*/_jsx(UIIcon, {
        className: "m-all-4",
        name: "insertImage",
        size: sticky ? 'sm' : 'md',
        color: FLINT
      }), /*#__PURE__*/_jsx(Big, {
        style: {
          color: FLINT
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_BASE}.logoPlaceholder`
        })
      })]
    });
  }, [logoUrl, name, sticky]);
  const scrollHandler = useCallback(() => requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setSticky(tabsOffsetTop !== null && (document.body.classList.contains('sticky-global-toolbar') ? document.getElementsByClassName('page')[0].scrollTop : window.pageYOffset) >= tabsOffsetTop);
    });
  }), [tabsOffsetTop]);
  useEffect(() => {
    const {
      top: tabsTop
    } = tabsRef.current.getBoundingClientRect();
    setTabsOffsetTop(tabsTop + (document.body.classList.contains('sticky-global-toolbar') ? document.getElementsByClassName('page')[0].scrollTop : window.pageYOffset));
  }, [tabsRef]);
  useEffect(() => {
    scrollHandler();
    window.addEventListener('scroll', scrollHandler, true);
    return () => window.removeEventListener('scroll', scrollHandler, true);
  }, [scrollHandler]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(BreadcrumbsWrapper, {
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        className: "p-y-4",
        children: /*#__PURE__*/_jsxs(UIFlex, {
          justify: "between",
          children: [renderedBreadcrumbs, renderedEditButton]
        })
      })
    }), /*#__PURE__*/_jsx(ScrollableHeaderWrapper, {
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        children: /*#__PURE__*/_jsxs(UIGrid, {
          children: [/*#__PURE__*/_jsx(UIGridItem, {
            size: PROFILE_LEFT_COL_SIZE,
            children: /*#__PURE__*/_jsx(LogoCardWrapper, {
              className: "flex-row align-center justify-center",
              children: /*#__PURE__*/_jsx(LogoImageWrapper, {
                children: renderedProfileImage
              })
            })
          }), /*#__PURE__*/_jsx(UIGridItem, {
            size: PROFILE_RIGHT_COL_SIZE,
            children: /*#__PURE__*/_jsx("div", {
              ref: tabsRef,
              children: renderedTabs
            })
          })]
        })
      })
    }), sticky && /*#__PURE__*/_jsx(MediaQuery, {
      minScreenSize: NAV_MIN_SCREEN_SIZE,
      children: /*#__PURE__*/_jsx(StickyHeaderWrapper, {
        isInApp: document.body.classList.contains('sticky-global-toolbar'),
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsxs(UIGrid, {
            className: "align-center",
            children: [/*#__PURE__*/_jsx(UIGridItem, {
              size: PROFILE_LEFT_COL_SIZE,
              children: renderedProfileImage
            }), /*#__PURE__*/_jsx(UIGridItem, {
              size: PROFILE_RIGHT_COL_SIZE,
              children: renderedTabs
            })]
          })
        })
      })
    })]
  });
};
ProfileHeader.propTypes = {
  activeTab: PropTypes.oneOf(['about', 'reviews']).isRequired,
  canonicalAppContext: CustomPropTypes.appContext.isRequired,
  isPrerender: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  profileListing: PropTypes.instanceOf(ProfileListing),
  scrollToTabSection: PropTypes.func.isRequired
};
export default ProfileHeader;